import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BarChart3, TrendingUp, Users, Clock, Globe, ArrowLeft, RefreshCw,
  Smartphone, Monitor, Tablet, Crown, Eye, MapPin, AlertCircle
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import useUser from "@/hooks/useUser";
import { PremiumLock, LoginRequired } from "@/components/PremiumLock";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AnalyticsPage() {
  const navigate = useNavigate();
  const { isPremium, isEnterprise, isLoggedIn, loading: userLoading } = useUser();
  
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState("30daysAgo");
  const [isDemo, setIsDemo] = useState(false);
  
  // Analytics data states
  const [overview, setOverview] = useState(null);
  const [trafficData, setTrafficData] = useState([]);
  const [sources, setSources] = useState([]);
  const [topPages, setTopPages] = useState([]);
  const [devices, setDevices] = useState([]);
  const [geoData, setGeoData] = useState([]);

  const fetchAnalytics = useCallback(async () => {
    if (!isLoggedIn || (!isPremium && !isEnterprise)) return;
    
    setLoading(true);
    try {
      const endDate = "today";
      const startDate = dateRange;
      
      const [overviewRes, trafficRes, sourcesRes, pagesRes, devicesRes, geoRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/analytics/overview?start_date=${startDate}&end_date=${endDate}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/analytics/traffic?start_date=${startDate}&end_date=${endDate}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/analytics/sources?start_date=${startDate}&end_date=${endDate}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/analytics/pages?start_date=${startDate}&end_date=${endDate}&limit=5`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/analytics/devices?start_date=${startDate}&end_date=${endDate}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/analytics/geo?start_date=${startDate}&end_date=${endDate}`, { withCredentials: true })
      ]);
      
      setOverview(overviewRes.data.data);
      setTrafficData(trafficRes.data.data || []);
      setSources(sourcesRes.data.data || []);
      setTopPages(pagesRes.data.data || []);
      setDevices(devicesRes.data.data || []);
      setGeoData(geoRes.data.data || []);
      
      // Check if any response is demo data
      setIsDemo(overviewRes.data.demo || false);
      
    } catch (error) {
      console.error("Error fetching analytics:", error);
      toast.error("Failed to load analytics data");
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn, isPremium, isEnterprise, dateRange]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const formatDuration = (seconds) => {
    if (!seconds) return "0s";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const getSourceColor = (channel) => {
    const colors = {
      'Organic Search': 'bg-green-500',
      'Direct': 'bg-blue-500',
      'Social': 'bg-pink-500',
      'Referral': 'bg-purple-500',
      'Paid Search': 'bg-orange-500',
      'Email': 'bg-yellow-500',
      'Display': 'bg-red-500'
    };
    return colors[channel] || 'bg-gray-500';
  };

  const getDeviceIcon = (device) => {
    const deviceLower = device?.toLowerCase() || '';
    if (deviceLower.includes('mobile')) return <Smartphone className="w-5 h-5" />;
    if (deviceLower.includes('tablet')) return <Tablet className="w-5 h-5" />;
    return <Monitor className="w-5 h-5" />;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/")} data-testid="back-button">
                <ArrowLeft className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Back</span>
              </Button>
              <span className="text-lg sm:text-xl font-bold bg-gradient-to-r from-violet-700 to-purple-600 bg-clip-text text-transparent">
                MarkeeMix
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-violet-100 text-violet-700">
                <Crown className="w-3 h-3 mr-1" />
                Pro
              </Badge>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Title */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
            <p className="text-sm text-gray-500">Google Analytics Data (Property ID: 423387739)</p>
          </div>
          
          {isLoggedIn && (isPremium || isEnterprise) && (
            <div className="flex items-center gap-3">
              {isDemo && (
                <Badge className="bg-yellow-100 text-yellow-700">
                  <AlertCircle className="w-3 h-3 mr-1" />
                  Demo Data
                </Badge>
              )}
              <Select value={dateRange} onValueChange={setDateRange}>
                <SelectTrigger className="w-40" data-testid="date-range-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7daysAgo">Last 7 Days</SelectItem>
                  <SelectItem value="30daysAgo">Last 30 Days</SelectItem>
                  <SelectItem value="90daysAgo">Last 90 Days</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={fetchAnalytics} disabled={loading}>
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          )}
        </div>

        {/* Auth Check */}
        {!isLoggedIn && !userLoading && (
          <LoginRequired message="Please login to access the Analytics Dashboard." />
        )}

        {/* Premium Check */}
        {isLoggedIn && !isPremium && !isEnterprise && (
          <PremiumLock 
            feature="Analytics Dashboard"
            description="Connect Google Analytics and view real-time insights about your website traffic. Upgrade to Pro to unlock."
          />
        )}

        {/* Dashboard Content */}
        {isLoggedIn && (isPremium || isEnterprise) && (
          <>
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <RefreshCw className="w-8 h-8 animate-spin text-violet-600" />
              </div>
            ) : (
              <>
                {/* Key Metrics */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 mb-6 sm:mb-8">
                  <Card className="p-3 sm:p-4">
                    <div className="flex items-center gap-2 mb-1 sm:mb-2">
                      <Users className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
                      <span className="text-xs text-gray-500">Users</span>
                    </div>
                    <p className="text-lg sm:text-2xl font-bold">{overview?.activeUsers?.toLocaleString() || 0}</p>
                  </Card>
                  
                  <Card className="p-3 sm:p-4">
                    <div className="flex items-center gap-2 mb-1 sm:mb-2">
                      <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
                      <span className="text-xs text-gray-500">Sessions</span>
                    </div>
                    <p className="text-lg sm:text-2xl font-bold">{overview?.sessions?.toLocaleString() || 0}</p>
                  </Card>
                  
                  <Card className="p-3 sm:p-4">
                    <div className="flex items-center gap-2 mb-1 sm:mb-2">
                      <Eye className="w-4 h-4 sm:w-5 sm:h-5 text-purple-500" />
                      <span className="text-xs text-gray-500">Page Views</span>
                    </div>
                    <p className="text-lg sm:text-2xl font-bold">{overview?.pageViews?.toLocaleString() || 0}</p>
                  </Card>
                  
                  <Card className="p-3 sm:p-4">
                    <div className="flex items-center gap-2 mb-1 sm:mb-2">
                      <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-orange-500" />
                      <span className="text-xs text-gray-500">Avg Duration</span>
                    </div>
                    <p className="text-lg sm:text-2xl font-bold">{formatDuration(overview?.avgSessionDuration)}</p>
                  </Card>
                  
                  <Card className="p-3 sm:p-4">
                    <div className="flex items-center gap-2 mb-1 sm:mb-2">
                      <Globe className="w-4 h-4 sm:w-5 sm:h-5 text-red-500" />
                      <span className="text-xs text-gray-500">Bounce Rate</span>
                    </div>
                    <p className="text-lg sm:text-2xl font-bold">{overview?.bounceRate?.toFixed(1) || 0}%</p>
                  </Card>
                  
                  <Card className="p-3 sm:p-4">
                    <div className="flex items-center gap-2 mb-1 sm:mb-2">
                      <Users className="w-4 h-4 sm:w-5 sm:h-5 text-teal-500" />
                      <span className="text-xs text-gray-500">New Users</span>
                    </div>
                    <p className="text-lg sm:text-2xl font-bold">{overview?.newUsers?.toLocaleString() || 0}</p>
                  </Card>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                  {/* Top Pages */}
                  <Card className="p-4 sm:p-6">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-violet-600" />
                      Top Pages
                    </h3>
                    <div className="space-y-3">
                      {topPages.map((page, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex-1 min-w-0 mr-3">
                            <p className="font-medium text-sm truncate">{page.path}</p>
                            <p className="text-xs text-gray-500 truncate">{page.title}</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="font-semibold">{page.pageViews?.toLocaleString()}</p>
                            <p className="text-xs text-gray-500">views</p>
                          </div>
                        </div>
                      ))}
                      {topPages.length === 0 && (
                        <p className="text-center text-gray-400 py-8">No page data available</p>
                      )}
                    </div>
                  </Card>

                  {/* Traffic Sources */}
                  <Card className="p-4 sm:p-6">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-green-600" />
                      Traffic Sources
                    </h3>
                    <div className="space-y-4">
                      {sources.slice(0, 5).map((source, idx) => {
                        const totalSessions = sources.reduce((sum, s) => sum + (s.sessions || 0), 0);
                        const percentage = totalSessions > 0 ? Math.round((source.sessions / totalSessions) * 100) : 0;
                        
                        return (
                          <div key={idx}>
                            <div className="flex justify-between text-sm mb-1">
                              <span>{source.channel} <span className="text-gray-400">({source.source})</span></span>
                              <span className="font-medium">{source.sessions?.toLocaleString()} ({percentage}%)</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className={`${getSourceColor(source.channel)} h-2 rounded-full transition-all`}
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                      {sources.length === 0 && (
                        <p className="text-center text-gray-400 py-8">No source data available</p>
                      )}
                    </div>
                  </Card>

                  {/* Devices */}
                  <Card className="p-4 sm:p-6">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <Monitor className="w-5 h-5 text-blue-600" />
                      Devices
                    </h3>
                    <div className="flex justify-around flex-wrap gap-4">
                      {devices.map((device, idx) => (
                        <div key={idx} className="text-center">
                          <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
                            {getDeviceIcon(device.device)}
                          </div>
                          <p className="text-xl sm:text-2xl font-bold">{device.percentage}%</p>
                          <p className="text-sm text-gray-500">{device.device}</p>
                          <p className="text-xs text-gray-400">{device.sessions?.toLocaleString()} sessions</p>
                        </div>
                      ))}
                      {devices.length === 0 && (
                        <p className="text-center text-gray-400 py-8 w-full">No device data available</p>
                      )}
                    </div>
                  </Card>

                  {/* Top Locations */}
                  <Card className="p-4 sm:p-6">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-red-600" />
                      Top Locations
                    </h3>
                    <div className="space-y-3">
                      {geoData.map((loc, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <span className="text-lg font-bold text-gray-400">{idx + 1}</span>
                            <div>
                              <p className="font-medium">{loc.country}</p>
                              <p className="text-xs text-gray-500">{loc.city}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold">{loc.sessions?.toLocaleString()}</p>
                            <p className="text-xs text-gray-500">sessions</p>
                          </div>
                        </div>
                      ))}
                      {geoData.length === 0 && (
                        <p className="text-center text-gray-400 py-8">No location data available</p>
                      )}
                    </div>
                  </Card>
                </div>

                {/* Traffic Trend Chart Placeholder */}
                <Card className="p-4 sm:p-6 mt-6">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-violet-600" />
                    Traffic Trend (Last {dateRange === "7daysAgo" ? "7" : dateRange === "30daysAgo" ? "30" : "90"} Days)
                  </h3>
                  <div className="h-48 flex items-end gap-1 overflow-x-auto pb-2">
                    {trafficData.slice(-30).map((day, idx) => {
                      const maxUsers = Math.max(...trafficData.map(d => d.users || 0), 1);
                      const height = Math.max(((day.users || 0) / maxUsers) * 100, 5);
                      
                      return (
                        <div key={idx} className="flex flex-col items-center min-w-[20px]">
                          <div 
                            className="w-4 bg-violet-500 rounded-t hover:bg-violet-600 transition-colors"
                            style={{ height: `${height}%` }}
                            title={`${day.date}: ${day.users} users`}
                          />
                          {idx % 5 === 0 && (
                            <span className="text-xs text-gray-400 mt-1 rotate-45 origin-left">
                              {day.date?.slice(5)}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {trafficData.length === 0 && (
                    <p className="text-center text-gray-400 py-8">No traffic data available</p>
                  )}
                </Card>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
